import { Vue, Component, Watch } from 'vue-property-decorator';
import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import { find, sortByProperty, truncateString } from '@/utils/utils';
import { taxonomyAPI } from '@/providers/apis/taxonomy/taxonomy';
import CrossWalkPopup from '../cross-walk-popup/cross-walk-popup';
import { sessionService } from '@/providers/services/auth/session';
import { DEMO_USER_ACCESS_CATEGORIES_LIST, CAP_SUBJECTS, CAP_CATEGORIES } from '@/utils/constants';
import axios from 'axios';
import { axisBottom } from 'd3';

@Component({
  name: 'cross-walk-table',
  components: {
    'material-icon': GoogleMaterialIcon,
    'crosswalk-popup': CrossWalkPopup,
  },
})

export default class CrossWalkTable extends Vue {


  private activeCategory: any = {};

  private activeSubject: any = {};

  private get activeframeWork() {
    return this.frameworkList.filter((item: any) => item.isActive);
  }

  private get contentList() {
    return {
      categories: this.categories,
      subjectList: this.subjectList,
      frameworkList: this.frameworkList,
    };
  }

  // --------------------------------------------------------------------------
  // Properties

  private categories: any = [];

  private subjectList: any = [];

  private frameworkList: any = [];

  private tableData: any = [];

  private demoCategories: any = DEMO_USER_ACCESS_CATEGORIES_LIST;

  @Watch('categories', { deep: true })
  private onChangeCategories(value: any) {
    const activeCategory: any = find(value, 'isActive') || find(value, 'is_default');
    this.activeCategory = activeCategory;
    this.onLoad(activeCategory.id);
  }

  @Watch('subjectList', { deep: true })
  private onChangeSujects(value: any) {
    let activeSubject: any = find(value, 'isActive') || find(value, 'is_default');
    if (!activeSubject) {
      activeSubject = value.find((item: any) => item.id === 'K12.MA');
      if (activeSubject) {
        activeSubject.isActive = true;
      }
    }
    this.activeSubject = activeSubject;
    this.frameworkList = activeSubject && activeSubject!.frameworks
      ? activeSubject!.frameworks : [];
  }

  private get isSuperUser() {
    const session = sessionService.getSession();
    return session!.permissions.indexOf('ALL_ACCESS') !== -1;
  }


  // -----------------------------------------------------------------------------
  // Hooks
  private created() {
    this.$store.dispatch('activityStore/fetchCategory').then(() => {
      this.categories = this.isSuperUser ? this.demoCategories : this.$store.state.activityStore.categoryList;
      // Demo purpose for CAP library
      if (this.$access.isCapLibrary) {
        this.categories = this.categories.map((cat: any) => {
          cat.isDisabled = true;
          if (CAP_CATEGORIES.indexOf(cat.id as any) !== -1) {
            cat.isDisabled = false;
          }
          return cat;
        });
      }
    });
  }

  // ----------------------------------------------------------------------------------------
  // Actions
  private onClickItems(value: any, selectedItem: any, key: string) {
    const actitveItem = find(value, 'isActive');
    const isFramework = key === 'frameworkList';
    if (actitveItem && !isFramework) {
      this.$set(actitveItem, 'isActive', false);
      this.frameworkList.filter((item: any) => item.isActive)
        .forEach((item: any) => this.$set(item, 'isActive', false));
    }
    const isActive = isFramework && selectedItem.isActive ? false : true;
    this.$set(selectedItem, 'isActive', isActive);

  }

  private generateTable() {
    if (this.$parent) {
      this.$parent.$data.isLoading = true;
    }
    const framewrokList = this.activeframeWork;
    const fwIds: any = framewrokList.map((item: any) => item.standard_framework_id);
    const subject = !find(this.subjectList, 'isActive')
      ? find(this.subjectList, 'is_default')
      : find(this.subjectList, 'isActive');
    const promiseList = [];
    framewrokList.forEach((framework: any) => {
      promiseList.push(taxonomyAPI.fetchCrossWalk(framework.standard_framework_id, subject.id, true));
    });
    promiseList.push(taxonomyAPI.fetchCrossWalk('GUT', subject.id, true));

    Promise.all(promiseList).then((data) => {
      this.updateTableContent({
        crosswalkData: data,
        frameworkList: fwIds,
      });
    });
  }



  // -----------------------------------------------------------------------------------------
  // Methods

  private onLoad(categoryId: string) {
    let categoryIds = [categoryId];
    if (this.isSuperUser) {
      categoryIds = this.demoCategories.find((item: any) => item.id === categoryId).included;

    }
    const promiseList = categoryIds.map((id: any) => taxonomyAPI.fetchSubjects(id));
    axios.all(promiseList).then((subjects) => {
      // Demo purpose for cap library
      this.subjectList = subjects.flat(1);
      if (this.$access.isCapLibrary) {
        this.subjectList.map((sub: any) => {
          sub.isDisabled = true;
          if (CAP_SUBJECTS.indexOf(sub.id) !== -1) {
            sub.isDisabled = false;
          }
          return sub;
        });
        this.subjectList = this.subjectList.sort((a: any, b: any) => {
          return CAP_SUBJECTS.indexOf(a.id) - CAP_SUBJECTS.indexOf(b.id);
        });
        sortByProperty(this.subjectList, 'isDisabled', 'ASC');
      }
    });
  }

  private activeClassName(value: any, items: any) {
    return find(value, 'isActive') ? items.isActive : items.is_default;
  }

  private updateTableContent(rawData: any) {
    const crosswalkData = rawData.crosswalkData;
    const frameworkList = rawData.frameworkList;
    const defaultHeaderItems = ['COMPETENCY'];
    const tableHeader = defaultHeaderItems.concat(frameworkList);
    const numberOfColumns = tableHeader.length;
    const tableBody: any = [];
    const crosswalkGUTData = crosswalkData.find((item: any) => item.id === 'GUT').data;
    crosswalkGUTData.map((data: any) => {
      const tableRowData: any = [];
      const competencyLevel = data.isMicro
        ? 'micro-competency hide-row'
        : 'competency';
      tableRowData.length = numberOfColumns;
      tableRowData.fill('');
      tableRowData.competencyType = competencyLevel;
      tableRowData[0] = {
        id: data.id,
        title: truncateString(data.title, 180),
        code: data.displayCode,
      };
      frameworkList.forEach((frameworkId: any) => {
        const frameworkPosition = tableHeader.indexOf(frameworkId);
        const competenciesData = crosswalkData.find((item: any) => item.id === frameworkId).data;
        if (competenciesData) {
          const competencyData = competenciesData.find((item: any) => item.key === data.id);
          if (competencyData) {
            const tableCellData = {
              id: competencyData.id,
              title: truncateString(competencyData.title, 180),
              code: competencyData.displayCode,
            };
            tableRowData[frameworkPosition] = tableCellData;
          }
        }
      });
      tableBody.push(tableRowData);
    });
    const tableData = {
      header: tableHeader,
      body: tableBody,
    };
    this.tableData = tableData;
    this.$emit('onGenerateTable', {
      tableData: this.tableData,
      category: this.activeCategory,
      subject: this.activeSubject,
    });
  }
}
